import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import PencomTextField from "@/components/input/PencomTextField";
import { useForm } from "@tanstack/react-form";
import { useLocation, useNavigate } from "react-router-dom";
// import { PencomGenericBanner } from "@/components/banners/PencomBanner";
import type { EmployerAuthorizedContactForm } from "@/forms/employer-authorized-contact/employer-authorized-contact.form";
// import PencomCheckBox from "@/components/input/PencomCheckBox";
import PencomDropdown from "@/components/input/PencomDropdown";
import { PencomConstants } from "@/constants/PencomConstants";
import { toast } from "@/utils/toast";
import { PencomRoutes } from "@/router/PencomRoutes";
import { useCreateAuthorizedContact } from "@/hooks/auth/auth.hook";
import { useEffect } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";

export const EmployerRepPage = () => {
  const navigate = useNavigate();
  const data = useLocation().state as {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    employerCode: string;
  };

  const { mutate: createAuthorizedContact, isPending } =
    useCreateAuthorizedContact();
  const { trackEvent, eventNames } = useAnalytics();

  const form = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      role: "",
      email: "",
      phoneNumber: "",
      preferredChannel: "Email",
      consentGiven: true,
    } as EmployerAuthorizedContactForm,
  });

  const handleCreateAuthorizedContact = () => {
    const trimmedEmail = form.state.values.email.trim();
    createAuthorizedContact(
      {
        employerCode: data?.employerCode,
        // userId: data?.data?.id,
        fullName: `${form.state.values.firstName} ${form.state.values.lastName}`,
        role: form.state.values.role,
        email: trimmedEmail,
        phone: form.state.values.phoneNumber,
        preferredChannel: form.state.values.preferredChannel,
        consentGiven: form.state.values.consentGiven,
      },
      {
        onSuccess: () => {
          navigate(PencomRoutes.EMPLOYER_REP_VERIFICATION, {
            state: {
              employerCode: data?.employerCode,
              email: trimmedEmail,
              phoneNumber: form.state.values.phoneNumber,
            },
          });
          // toast.success("Authorized contact created successfully");
          trackEvent(eventNames.onboardingStepSubmitted, {
            step: "employer_rep",
            status: "success",
          });
        },
        onError: (error) => {
          toast.error(error.message);
          trackEvent(eventNames.onboardingStepSubmitted, {
            step: "employer_rep",
            status: "error",
            message: error.message,
          });
        },
      }
    );
  };
  useEffect(() => {
    trackEvent(eventNames.onboardingPageView, {
      step: "employer_rep",
    });
  }, []);
  return (
    <div className="w-full flex flex-col">
      <h2 className="font-clash-display font-semibold text-[40px] text-medium-dark-grey leading-[1.25] tracking-[-0.6px]">
        Contact Person
      </h2>
      <p className="font-clash-display font-normal text-base text-medium-dark-grey leading-[1.25] tracking-[-0.24px] mt-3">
        This person will manage pension compliance, apply for PCC, and receive
        official notifications from PenCom.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="flex flex-col mt-8 gap-6"
      >
        <p className="font-clash-display font-semibold text-lg text-medium-dark-grey tracking-[-0.08px]">
          Representative Details
        </p>
        <div className="flex flex-row gap-4">
          <form.Field
            name="firstName"
            validators={{
              onChange: ({ value }) =>
                !value ? "First name is required." : undefined,
            }}
            children={(field) => (
              <div className="flex-1">
                <PencomTextField
                  id="rep-first-name"
                  label="First name"
                  placeholder="Enter first name"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  error={!!field.state.meta.errors.length}
                  helperText={field.state.meta.errors.join(", ")}
                />
              </div>
            )}
          />
          <form.Field
            name="lastName"
            validators={{
              onChange: ({ value }) =>
                !value ? "Last name is required." : undefined,
            }}
            children={(field) => (
              <div className="flex-1">
                <PencomTextField
                  id="rep-last-name"
                  label="Last name"
                  placeholder="Enter last name"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  error={!!field.state.meta.errors.length}
                  helperText={field.state.meta.errors.join(", ")}
                />
              </div>
            )}
          />
        </div>
        <form.Field
          name="role"
          validators={{
            onChange: ({ value }) =>
              !value ? "Job Title is required." : undefined,
          }}
          children={(field) => (
            <div className="flex-1">
              <PencomDropdown
                id="role"
                label="Job Title"
                placeholder="Select job title"
                options={PencomConstants.AUTHORIZED_CONTACT_ROLES.map(
                  (role) => ({
                    id: role,
                    label: role,
                  })
                )}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                helperText={
                  field.state.meta.errors.length > 0
                    ? field.state.meta.errors[0]
                    : undefined
                }
              />
            </div>
          )}
        />

        <p className="font-clash-display font-semibold text-lg text-medium-dark-grey tracking-[-0.08px] mt-4">
          Contact Details
        </p>
        <form.Field
          name="email"
          validators={{
            onChange: ({ value }) => {
              if (!value) return "Email Address is required.";
              if (!/\S+@\S+\.\S+/.test(value)) return "Invalid email address.";
              return undefined;
            },
          }}
          children={(field) => (
            <div className="flex-1">
              <PencomTextField
                id="email-address"
                label="Email Address"
                placeholder="Enter email address"
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                error={!!field.state.meta.errors.length}
                helperText={field.state.meta.errors.join(", ")}
              />
            </div>
          )}
        />
        <div className="flex gap-2 items-start">
          <img
            src="/assets/icons/info-outline.svg"
            alt=""
            className="w-4 h-4 shrink-0 mt-0.5"
            loading="lazy"
            aria-hidden
          />
          <p className="font-clash-display font-normal text-sm text-medium-grey leading-5 tracking-[-0.07px]">
            This email will be used to sign in to eHub and receive official
            notifications. <br />{" "}
            <span className="font-semibold text-medium-grey">
              It must not already be linked to another employer account.
            </span>
          </p>
        </div>
        {/* <PencomGenericBanner className="border-0 p-0 m-0" children={
                        <div className='flex flex-col'>
                            <p className='font-regular font-clash-display text-sm text-medium-dark-grey'>
                                Login codes will be sent here. Please ensure address is correct and accessible.
                            </p>
                        </div>
                    } /> */}
        <form.Field
          name="phoneNumber"
          validators={{
            onChange: ({ value }) => {
              if (!value) return "Phone Number is required.";
              if (!/^\d{11}$/.test(value))
                return "Phone Number must be 11 digits.";
              return undefined;
            },
          }}
          children={(field) => (
            <div className="flex-1">
              <PencomTextField
                id="phone-number"
                label="Phone Number"
                placeholder="Enter phone number"
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                error={!!field.state.meta.errors.length}
                helperText={field.state.meta.errors.join(", ")}
              />
            </div>
          )}
        />
        <span className="text-xs font-regular font-clash-display text-medium-grey">
          By continuing, you confirm that this person is authorised to act on
          behalf of your organisation.
        </span>
        <form.Subscribe
          children={(state) => {
            const isDisabled =
              !state.canSubmit ||
              !state.values.firstName?.length ||
              !state.values.lastName?.length ||
              !state.values.role?.length ||
              !state.values.email?.length ||
              !state.values.phoneNumber?.length;
            return (
              <PrimaryButton
                isLoading={isPending}
                disabled={isDisabled}
                className="mt-2 w-full"
                title="Verify Email"
                onClick={handleCreateAuthorizedContact}
              />
            );
          }}
        />
      </form>
    </div>
  );
};
